import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CalendarDays, CheckCircle2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { handleGoogleCalendarCallback } from '@/lib/api'
import { BUTTON_STYLES } from '@/lib/buttonStyles'

const GoogleCalendarCallback = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    const state = queryParams.get('state')
    const errorParam = queryParams.get('error')
    const errorDescription = queryParams.get('error_description')

    const processCallback = async () => {
      if (errorParam) {
        setError(errorDescription || 'Google Calendar access was not granted.')
        setStatus('error')
        return
      }

      if (!code) {
        setError('Something went wrong during the connection. Please try again.')
        setStatus('error')
        return
      }

      try {
        await handleGoogleCalendarCallback({ code, state: state || undefined })
        setStatus('success')
      } catch (err: any) {
        console.error('Error processing Google Calendar callback:', err)
        setError(err.message || 'Something went wrong. Please try again.')
        setStatus('error')
      }
    }

    processCallback()
  }, [location.search])

  if (status === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6 pb-10 pt-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-linear-to-br from-[#00c950] to-[#009966] shadow-[0_10px_24px_rgba(0,201,80,0.25)]">
          <svg className="h-6 w-6 animate-spin text-white" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold text-white">Connecting your calendar…</h1>
          <p className="text-sm text-[#99a1af]">
            Just a moment while we finish setting things up.
          </p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6 pb-10 pt-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-[20px] border border-red-500/25 bg-red-500/10">
          <XCircle className="h-8 w-8 text-red-400" />
        </div>
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold text-white">Connection failed</h1>
          <p className="text-sm text-[#99a1af]">{error}</p>
        </div>
        <div className="flex w-full flex-col gap-3">
          <Button
            onClick={() => navigate('/connect/google-calendar')}
            className={`h-12 w-full rounded-[14px] text-base font-semibold ${BUTTON_STYLES.primary}`}
          >
            Try again
          </Button>
          <Button
            onClick={() => navigate('/calendar')}
            className={`h-12 w-full rounded-[14px] text-base font-semibold ${BUTTON_STYLES.secondary}`}
          >
            Go to Calendar
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6 pb-10 pt-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-linear-to-br from-[#00c950] to-[#009966] shadow-[0_10px_24px_rgba(0,201,80,0.25)]">
        <CheckCircle2 className="h-8 w-8 text-white" />
      </div>
      <div className="space-y-1.5">
        <h1 className="text-2xl font-bold text-white">Google Calendar connected</h1>
        <p className="text-sm text-[#99a1af]">
          Your schedule will now appear alongside your NIL deadlines.
        </p>
      </div>
      <div className="w-full rounded-2xl border border-[#1e2939] bg-[#131824] p-5 text-left">
        <div className="flex items-start gap-3">
          <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-[#8ec5ff]" />
          <p className="text-sm leading-5 text-[#99a1af]">
            Head to calendar settings any time to choose which calendars to sync or to disconnect
            your account.
          </p>
        </div>
      </div>
      <Button
        onClick={() => navigate('/calendar')}
        className={`h-12 w-full rounded-[14px] text-base font-semibold ${BUTTON_STYLES.primary}`}
      >
        Go to Calendar
      </Button>
    </div>
  )
}

export default GoogleCalendarCallback
